/**
 * @file XeroReports.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-23
 * @description Reports → Xero Reports: the six reports Cam used to export from
 *              Xero to Excel — Accrual, Despatch, AHDB Levy, Stock Pricing,
 *              Customer Tonnage and the month-end Price Check — from this
 *              system's own records, one tab each (Dashboard TABBED_PAGES
 *              'report-xero').
 *
 *              Each opens with the filters Xero's export had, worked out for
 *              today by the server (GET /api/xero-reports/definitions); the
 *              filters on screen are kept per report across navigation. The
 *              table is grouped as the export was (by PO, contact or item
 *              code) with a total under each group and a grand total; Export to
 *              Excel downloads the same report laid out like Xero's
 *              (GET /api/xero-reports/:key/export — codes as text).
 *
 *              Stock Pricing's item codes are the saved stock range, which
 *              anyone here can change ("Save as our stock range"). Purchase
 *              costs on the PO reports need "See cost & margin"; the server
 *              leaves those columns out without it.
 *
 * 1.1.0 (15 Oct 2026): Accrual / Despatch say which financial year start they
 * use (Xero's year end, else 1 April); invoice reports leave voided and
 * deleted out unless "Show voided and deleted" is on.
 *
 * 1.3.0 (23 Oct 2026, Cam): Despatch lists only lines with no supplied
 * quantity recorded ("Only without supplied qty", on by default) and says how
 * many it left out; every PO report shows the ordered quantity in its own unit
 * (QtyWithTonnes — "40 × 25kg bag") with a Tonnes column beside it.
 *
 * 1.2.1 (16 Oct 2026, Cam): bigger than a laptop, the Suppliers / Customers
 * and Items pickers share the row with the other filters (CSS only).
 *
 * 1.2.0 (15 Oct 2026, Cam's list):
 *  - Column filters on every header (right-click), as the search screens —
 *    server-side, so the counts, pages and totals follow them; Clear Filters
 *    clears them too; the pill names what is narrowing the report.
 *  - The search screens' layout: the body scrolls inside the table, the header
 *    stays put (sticky), the grand total stays in sight at the foot; margins as
 *    the search screens.
 *  - Pages of 50 lines (PaginationControls). Group subtotals and the grand
 *    total are always the WHOLE filtered result's, and the screen says so; a
 *    group cut by the page shows "(continued)" and its subtotal where it ends.
 *    Export to Excel is always the whole result.
 *  - Every dropdown in the filters searchable and multi-select: statuses,
 *    suppliers / customers, items (code + name; Stock Pricing's range is
 *    chosen the same way and still saved for everyone).
 *  - The period: a labelled preset (This month / Last month / Last quarter /
 *    Financial year to date / Custom…) before From / To (DD/MM/YYYY);
 *    choosing one fills the dates, typing a date makes it Custom.
 *  - AHDB Levy: self-billed purchases only, with the evidence in its own
 *    column and a switch to see them all.
 *
 *  Each report is its own component (keyed by report), so its filters, page and
 *  column filters persist under its own keys and nothing leaks between tabs.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './XeroReports.css';
import DateRangeFilter from './DateRangeFilter';
import DateInput from './DateInput';
import MultiSelectDropdown from './MultiSelectDropdown';
import LoadingSpinner from './LoadingSpinner';
import PaginationControls from './PaginationControls';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import EntityLink from './EntityLink';
import QtyWithTonnes from './QtyWithTonnes';
import { showAlert } from './Alert';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import useColumnFilters from '../hooks/useColumnFilters';
import { clearFiltersClass } from '../utils/activeFilters';
import { formatDateShort } from '../utils/dateFormatters';
import {
  filtersFromDefaults, reportQuery, headerFiltersChanged, cellText, columnOptionLabel,
  isNumeric, contactOptions, itemOptions, pageLines, fileNameFrom, PAGE_SIZE
} from '../utils/xeroReports';

const getJson = async (url, init) => {
  const res = await fetch(url, { credentials: 'include', ...init });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `The server answered ${res.status}`);
  return data;
};

const n = (v) => Number(v || 0).toLocaleString('en-GB');
const plural = (count, word) => `${n(count)} ${word}${count === 1 ? '' : 's'}`;

/** A cell, made clickable where it names a document or a contact. */
function Cell({ col, row }) {
  // A PO line's quantity in the unit it was ordered in (its tonnes are the
  // Tonnes column beside it).
  if (col.key === 'quantity' && row.po_id) {
    return <QtyWithTonnes quantity={row.quantity} line={row} unitsOnly empty="" />;
  }
  const text = cellText(col, row[col.key]);
  if (col.key === 'invoice_number' && row.invoice_id) {
    return <EntityLink type={row.is_credit ? 'credit_note' : 'invoice'} id={row.invoice_id}>{text}</EntityLink>;
  }
  if (col.key === 'order_number' && row.po_xero_id) {
    return <EntityLink type="purchase_order" id={row.po_xero_id}>{text}</EntityLink>;
  }
  if (col.key === 'contact' && row.contact_id) {
    return <EntityLink type="contact" id={row.contact_id}>{text}</EntityLink>;
  }
  if (col.key === 'made_in') return <span className={`made-in${row.made_in === 'Xero' ? '' : ' here'}`}>{text}</span>;
  if (col.key === 'self_billed_by') return <span className="self-billed-by">{text}</span>;
  return text;
}

/** One toggle in the filters (the system's toggle switch). */
function Toggle({ id, label, checked, onChange, title }) {
  return (
    <div className="filter-group toggle-cell" title={title}>
      <label htmlFor={id}>{label}</label>
      <div className="checkbox-container">
        <div className="check light">
          <input id={id} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
          <label htmlFor={id}></label>
        </div>
      </div>
    </div>
  );
}

/** How a multi-select names what is chosen. */
const summarizeAs = (none, one, many) => (value, options) => {
  if (!value || !value.length) return none;
  if (value.length === 1) return one(options.find(o => o.value === value[0]) || { value: value[0] });
  return many(value.length);
};

function XeroReportView({ def, canSeeCosts, stockRange, setStockRange, financialYear, periodPresets, options, loadOptions }) {
  const key = def.key;
  const isPo = def.family === 'po';
  const defaults = useMemo(() => filtersFromDefaults(def.defaults), [def.defaults]);
  const [filters, setFilterState] = usePersistedState(`xeroReports.${key}.filters`, defaults, { merge: true });
  const [page, setPage] = usePersistedState(`xeroReports.${key}.page`, 1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [savingRange, setSavingRange] = useState(false);
  const latest = useLatestRequest();

  const shown = result && result.key === key ? result : null;
  const columns = shown?.columns || def.columns.filter(c => canSeeCosts || !c.cost);
  const colDefs = useMemo(() => Object.fromEntries(columns.map(c => [c.key, { label: c.label }])), [columns]);
  const optionLabel = useCallback((k, v) => columnOptionLabel(columns, k, v), [columns]);
  const cols = useColumnFilters([], colDefs, {
    facets: shown?.facets || {},
    storageKey: `xeroReports.${key}.columnFilters`,
    onChange: () => setPage(1),
    optionLabel
  });

  useSlideOutButton([loading, exporting, savingRange, key, shown?.rowCount]);

  // The dropdowns' contacts and items, once per side.
  useEffect(() => { loadOptions(def.family); }, [def.family, loadOptions]);

  const setFilters = (patch) => { setFilterState(prev => ({ ...prev, ...patch })); setPage(1); };
  const headerFilters = headerFiltersChanged(filters, def);
  const applied = headerFilters.count > 0 || cols.activeCount > 0;
  const handleClearFilters = () => {
    setFilterState(defaults);
    cols.clearEverything();
    setPage(1);
  };

  const query = reportQuery(def, filters, { columnFilters: cols.queryValue });
  const pageQuery = `${query}&page=${page || 1}&limit=${PAGE_SIZE}`;

  const runReport = useCallback(async () => {
    const ticket = latest.begin();
    const isLatest = () => latest.isLatest(ticket);
    setLoading(true);
    setError(null);
    try {
      const data = await getJson(`${API_URL}/api/xero-reports/${key}?${pageQuery}`);
      if (!isLatest()) return;
      setResult(data);
      // A page past the end of a smaller result comes back as the last page.
      if (data.pagination && data.pagination.page !== (page || 1)) setPage(data.pagination.page);
    } catch (e) {
      if (isLatest()) { setResult(null); setError(e.message); }
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [key, pageQuery, page, latest, setPage]);

  // Typing settles before the report runs again.
  useEffect(() => {
    const t = setTimeout(runReport, 350);
    return () => clearTimeout(t);
  }, [runReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/xero-reports/${key}/export?${query}`, { credentials: 'include' });
      if (!res.ok) {
        let message = `the server answered ${res.status}`;
        try { message = (await res.json()).error || message; } catch (e) { /* not JSON */ }
        throw new Error(message);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileNameFrom(res, `${def.title}.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoking synchronously cancels the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      showAlert(`The spreadsheet could not be made: ${e.message}`, 'error');
    } finally {
      setExporting(false);
    }
  };

  // ── The stock range (Stock Pricing) ──
  const savedCodes = stockRange?.codes || [];
  const chosenCodes = filters.itemCodes || [];
  const sameCodes = (a, b) => [...a].map(c => c.toUpperCase()).sort().join(',') === [...b].map(c => c.toUpperCase()).sort().join(',');
  const rangeDiffers = key === 'stock-pricing' && !sameCodes(chosenCodes, savedCodes);
  const saveRange = async () => {
    setSavingRange(true);
    try {
      const saved = await getJson(`${API_URL}/api/xero-reports/stock-range`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes: chosenCodes })
      });
      setStockRange(saved);
      showAlert(`Stock range saved — ${plural(saved.codes.length, 'item code')}`, 'success');
    } catch (e) {
      showAlert(`The stock range was not saved: ${e.message}`, 'error');
    } finally {
      setSavingRange(false);
    }
  };

  // ── The dropdowns ──
  const statusOptions = def.statuses.map(s => ({ value: s.value, label: s.label, searchText: s.label }));
  const sideOptions = options?.[def.family];
  const contactOpts = useMemo(() => contactOptions(sideOptions?.contacts || []).map(o => ({
    ...o,
    label: (
      <div className="filter-option-coded">
        {o.accountNo && <span className="product-code">{o.accountNo}</span>}
        <span className="option-title">{o.name}{o.archived ? ' (archived)' : ''}</span>
      </div>
    )
  })), [sideOptions]);
  const itemOpts = useMemo(() => itemOptions(options?.items || [], chosenCodes).map(o => ({
    ...o,
    label: (
      <div className="filter-option-coded">
        <span className="product-code">{o.code}</span>
        <span className="option-title">{o.unknown ? 'not an item here' : o.name}{o.archived ? ' (archived)' : ''}</span>
      </div>
    )
    // chosenCodes is a fresh array each render; its contents are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [options?.items, chosenCodes.join('|')]);

  const side = isPo ? 'supplier' : 'customer';
  const totalKeys = new Set(def.totals);
  const totalsCell = (totals, col) => (totalKeys.has(col.key) ? cellText(col, totals?.[col.key]) : '');
  const flagged = shown?.flaggedCount;
  const pagination = shown?.pagination;
  const groups = shown?.groups || [];
  const showItems = def.filters.includes('itemCodes') || def.filters.includes('stockRange');
  const needsChoice = def.needsContact && !(filters.contactIds || []).length;

  return (
    <div className="xero-reports">
      <div className="search-header">
        <div className="workflow-info">
          <span className="info">{def.title}</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span className="note-text">
              {def.description} As Xero&apos;s &ldquo;{def.xeroName}&rdquo;, from this system&apos;s records.
              {isPo && financialYear?.start && (key === 'accrual' || key === 'despatch') && (
                <> The financial year&apos;s first day is {formatDateShort(financialYear.start)}
                  {financialYear.fromXero ? " (from Xero's year end)" : " (Xero's year end not read yet — 1 April assumed)"}.</>
              )}
            </span>
          </span>
        </div>
        <div className="search-header-inner">
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(applied)}
                onClick={handleClearFilters}
                title={applied ? "Back to the export's filters, and clear the column filters" : "Showing the export's filters"}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button
                className="btn-slideout"
                onClick={handleExport}
                disabled={exporting || loading || !shown || shown.rowCount === 0}
                title={!shown || shown.rowCount === 0 ? 'Nothing to export' : `Download all ${n(shown.rowCount)} lines as an Excel workbook`}
              >
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded ${exporting ? 'spinning' : ''}`}>table_view</span>
                </div>
                <span className="btn-label">{exporting ? 'Exporting…' : 'Export to Excel'}</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={runReport} disabled={loading}>
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>sync</span>
                </div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
          <div className="search-stats">
            {shown ? `${plural(shown.rowCount, 'line')} · ${plural(pagination?.groupCount ?? groups.length, 'group')}` : ''}
            {flagged != null && ` · ${n(flagged)} to check`}
          </div>
        </div>
      </div>

      <div className={`xero-reports-filters family-${def.family}`}>
        <div className="filter-row xr-period">
          <DateRangeFilter
            from={filters.from}
            to={filters.to}
            preset={filters.preset}
            presets={periodPresets}
            presetFirst
            presetLabel="Period"
            fromLabel={isPo ? 'PO date from' : 'Invoice date from'}
            toLabel="To"
            onChange={({ from, to, preset }) => setFilters({ from, to, preset })}
          />
        </div>
        {isPo && (
          <div className="filter-row xr-due">
            <div className="filter-group">
              <DateInput label="Due from" value={filters.dueFrom} onChange={(e) => setFilters({ dueFrom: e.target.value })} />
            </div>
            <div className="filter-group">
              <DateInput label="Due to" value={filters.dueTo} onChange={(e) => setFilters({ dueTo: e.target.value })} />
            </div>
          </div>
        )}
        <div className="filter-group xr-status">
          <label>Status</label>
          <MultiSelectDropdown
            options={statusOptions}
            value={filters.statuses}
            onChange={(v) => setFilters({ statuses: v })}
            searchable
            searchPlaceholder="Search statuses…"
            hideTags
            summarize={summarizeAs('Choose statuses…', (o) => o.label || o.value,
              (count) => (count === statusOptions.length ? 'Every status' : `${count} statuses`))}
          />
        </div>
        <div className="xr-toggles">
          {def.filters.includes('haulage') && (
            <Toggle id={`xr-haulage-${key}`} label="Haulage lines" checked={filters.includeHaulage}
                    onChange={(v) => setFilters({ includeHaulage: v })}
                    title="Direct Delivery haulage lines (money owed to the supplier for carriage)" />
          )}
          {def.filters.includes('selfBilledOnly') && (
            <Toggle id={`xr-self-billed-${key}`} label="Only self-billed" checked={filters.selfBilledOnly}
                    onChange={(v) => setFilters({ selfBilledOnly: v })}
                    title="Purchases we wrote the grower's invoice for: the PO's SELF-BILLED switch, a bill numbered as the PO, or the levy deducted on the PO" />
          )}
          {def.filters.includes('unsuppliedOnly') && (
            <Toggle id={`xr-unsupplied-${key}`} label="Only without supplied qty" checked={filters.unsuppliedOnly}
                    onChange={(v) => setFilters({ unsuppliedOnly: v })}
                    title="Leave out lines whose supplied quantity has been recorded (the order line's, or a stock PO line's own) — what is still to come in" />
          )}
          {def.filters.includes('includeVoided') && (
            <Toggle id={`xr-voided-${key}`} label="Show voided and deleted" checked={filters.includeVoided}
                    onChange={(v) => setFilters({ includeVoided: v })} />
          )}
          {def.filters.includes('onlyFlagged') && (
            <Toggle id={`xr-only-flagged-${key}`} label="Only lines to check" checked={filters.onlyFlagged}
                    onChange={(v) => setFilters({ onlyFlagged: v })} />
          )}
        </div>
        {/* On a laptop, who and what on a line of their own, below the dates
            and switches; bigger than a laptop the break is hidden and they
            share the row (XeroReports.css, utils/displaySize). */}
        <div className="xr-break" aria-hidden="true" />
        <div className={`filter-group xr-contacts${needsChoice ? ' needs-choice' : ''}`}>
          <label>{def.needsContact ? 'Customer' : (isPo ? 'Suppliers' : 'Customers')}</label>
          <MultiSelectDropdown
            options={contactOpts}
            value={filters.contactIds || []}
            onChange={(v) => setFilters({ contactIds: v })}
            searchable
            selectedInMenu
            hideTags
            searchPlaceholder="Search by # or name…"
            emptyMessage={sideOptions ? 'No contacts match' : 'Loading…'}
            summarize={summarizeAs(def.needsContact ? 'Choose a customer…' : `All ${side}s`, (o) => o.name || o.value,
              (count) => `${count} ${side}s`)}
          />
        </div>
        {showItems && (
          <div className="filter-group xr-items">
            <label>{key === 'stock-pricing' ? 'Stock range (items)' : 'Items'}</label>
            <MultiSelectDropdown
              options={itemOpts}
              value={chosenCodes}
              onChange={(v) => setFilters({ itemCodes: v })}
              searchable
              selectedInMenu
              hideTags
              searchPlaceholder="Search by code or name…"
              emptyMessage={options?.items ? 'No items match' : 'Loading…'}
              summarize={summarizeAs('Any item', (o) => (o.name ? `${o.code} · ${o.name}` : o.value),
                (count) => `${count} items`)}
            />
          </div>
        )}
      </div>

      {key === 'stock-pricing' && (
        <div className="xero-reports-range">
          <span className="material-symbols-rounded">inventory_2</span>
          <span className="range-text">
            {stockRange?.saved
              ? `Our stock range: ${plural(savedCodes.length, 'item code')}, saved${stockRange.updatedBy ? ` by ${stockRange.updatedBy}` : ''}${stockRange.updatedAt ? ` on ${formatDateShort(stockRange.updatedAt)}` : ''}.`
              : `Our stock range: the ${plural(savedCodes.length, 'item code')} of Xero's Stock Pricing List (not yet saved here).`}
            {rangeDiffers && ' The items chosen above differ from it.'}
          </span>
          <div className="buttons slideout">
            <div>
              <button className="btn-slideout" onClick={() => setFilters({ itemCodes: [...savedCodes] })}
                      disabled={!rangeDiffers} title={rangeDiffers ? 'Choose the saved range again' : 'The saved range is chosen'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">undo</span></div>
                <span className="btn-label">Use saved range</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={saveRange}
                      disabled={savingRange || !chosenCodes.length || (!rangeDiffers && stockRange?.saved)}
                      title={!chosenCodes.length ? 'Choose at least one item' : 'Keep these items as our stock range, for everyone'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
                <span className="btn-label">{savingRange ? 'Saving…' : 'Save as our stock range'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {shown?.suppliedLeftOut?.lines > 0 && (
        <div className="xero-reports-note">
          <span className="material-symbols-rounded">filter_alt</span>
          {plural(shown.suppliedLeftOut.lines, 'line')} on {plural(shown.suppliedLeftOut.purchaseOrders, 'PO')} with a supplied
          {' '}quantity recorded {shown.suppliedLeftOut.lines === 1 ? 'is' : 'are'} left out — turn off &ldquo;Only without supplied qty&rdquo; to see {shown.suppliedLeftOut.lines === 1 ? 'it' : 'them'}.
        </div>
      )}

      {shown?.notSelfBilled?.lines > 0 && (
        <div className="xero-reports-note">
          <span className="material-symbols-rounded">filter_alt</span>
          {plural(shown.notSelfBilled.lines, 'line')} on {plural(shown.notSelfBilled.purchaseOrders, 'PO')} not self-billed
          {' '}(a merchant&apos;s own invoice) {shown.notSelfBilled.lines === 1 ? 'is' : 'are'} left out — turn off &ldquo;Only self-billed&rdquo; to see {shown.notSelfBilled.lines === 1 ? 'it' : 'them'}.
        </div>
      )}

      {shown?.costsHidden && (
        <div className="xero-reports-note">
          <span className="material-symbols-rounded">visibility_off</span>
          Purchase costs are left out — they need &ldquo;See cost &amp; margin&rdquo;.
        </div>
      )}

      <div className="table-toolbar xero-reports-toolbar">
        <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />
        {shown && shown.rowCount > 0 && (
          <span className="totals-scope" title="Group totals and the grand total count every line on these filters, on every page">
            <span className="material-symbols-rounded">functions</span>
            Totals are for all {plural(shown.rowCount, 'line')}
          </span>
        )}
        {pagination && pagination.total > 0 && (
          <PaginationControls
            pagination={pagination}
            onPageChange={(p) => setPage(p)}
          />
        )}
      </div>

      <div className="table-container">
        <table className="data-table table-min-xl xero-reports-table">
          <thead>
            <tr>
              {columns.map(c => {
                const hp = cols.headerProps(c.key);
                return (
                  <th key={c.key}
                      data-col={c.key}
                      className={[isNumeric(c) ? 'num' : '', hp.className || ''].filter(Boolean).join(' ') || undefined}
                      onContextMenu={hp.onContextMenu}
                      title="Right-click to filter this column">
                    {c.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {shown?.needsContact ? (
              <tr><td colSpan={columns.length} className="no-results">Choose a customer to see their tonnage.</td></tr>
            ) : error ? (
              <tr><td colSpan={columns.length} className="no-results">{error}</td></tr>
            ) : loading && !shown ? (
              <tr><td colSpan={columns.length}><LoadingSpinner /></td></tr>
            ) : !shown || shown.rowCount === 0 ? (
              <tr><td colSpan={columns.length} className="no-results">Nothing on these filters.</td></tr>
            ) : groups.map(g => (
              <React.Fragment key={g.key}>
                <tr className="group-row">
                  <td colSpan={columns.length}>
                    {g.label}
                    {g.continued && <span className="group-continued"> (continued)</span>}
                    {g.lineCount != null && <span className="group-count">{plural(g.lineCount, 'line')}</span>}
                  </td>
                </tr>
                {g.rows.map(r => (
                  <tr key={r.id} className={r.flagged ? 'price-flag' : ''}>
                    {columns.map(c => (
                      <td key={c.key}
                          data-col={c.key}
                          className={`${isNumeric(c) ? 'num' : ''}${c.key === 'check' && r.flagged ? ' check-cell' : ''}`}>
                        <Cell col={c} row={r} />
                      </td>
                    ))}
                  </tr>
                ))}
                {g.ends !== false && (
                  <tr className="subtotal-row">
                    {columns.map((c, i) => (
                      <td key={c.key} className={totalKeys.has(c.key) ? 'num' : ''}>
                        {i === 0 ? `Total ${g.label}` : totalsCell(g.totals, c)}
                      </td>
                    ))}
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          {shown && shown.rowCount > 0 && (
            <tfoot>
              <tr className="grand-total">
                {columns.map((c, i) => (
                  <td key={c.key} className={totalKeys.has(c.key) ? 'num' : ''}
                      title={i === 0 ? `Every line on these filters (${pageLines(pagination) || plural(shown.rowCount, 'line')} shown)` : undefined}>
                    {i === 0 ? `Total — all ${plural(shown.rowCount, 'line')}` : totalsCell(shown.totals, c)}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <ColumnFilterMenu cols={cols} />
    </div>
  );
}

function XeroReports({ activeTab }) {
  const [defs, setDefs] = useState(null);
  const [canSeeCosts, setCanSeeCosts] = useState(true);
  const [stockRange, setStockRange] = useState(null);
  const [financialYear, setFinancialYear] = useState(null);
  const [periodPresets, setPeriodPresets] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [options, setOptions] = useState({});
  const asked = useRef({});

  // The definitions, once.
  useEffect(() => {
    let alive = true;
    getJson(`${API_URL}/api/xero-reports/definitions`)
      .then((data) => {
        if (!alive) return;
        setDefs(data.reports || []);
        setCanSeeCosts(!!data.canSeeCosts);
        setStockRange(data.stockRange || null);
        setFinancialYear(data.financialYear || null);
        setPeriodPresets(data.periodPresets || null);
      })
      .catch((e) => alive && setLoadError(e.message));
    return () => { alive = false; };
  }, []);

  // The dropdowns' contacts (per side) and items, each asked for once.
  const loadOptions = useCallback((family) => {
    if (!family || asked.current[family]) return;
    asked.current[family] = true;
    getJson(`${API_URL}/api/xero-reports/options?family=${family}`)
      .then((data) => setOptions(prev => ({
        ...prev,
        [family]: { contacts: data.contacts || [] },
        items: prev.items || data.items || []
      })))
      .catch(() => {
        asked.current[family] = false;
        setOptions(prev => ({ ...prev, [family]: { contacts: [] }, items: prev.items || [] }));
      });
  }, []);

  const def = useMemo(() => (defs || []).find(d => d.key === activeTab) || (defs || [])[0] || null, [defs, activeTab]);

  if (loadError) return <div className="xero-reports"><div className="no-results">Could not load the reports: {loadError}</div></div>;
  if (!def) return <div className="xero-reports"><LoadingSpinner /></div>;

  return (
    <XeroReportView
      key={def.key}
      def={def}
      canSeeCosts={canSeeCosts}
      stockRange={stockRange}
      setStockRange={setStockRange}
      financialYear={financialYear}
      periodPresets={periodPresets}
      options={options}
      loadOptions={loadOptions}
    />
  );
}

export default XeroReports;
